import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { RoutePaths } from '../../../routes/AppRoutes';
import { PackagingFamilyTypeIntl } from '../../../common/i18n/product-family.translation';
import { ProductFragment } from '../../products';
import ProductUtils, { isNonFoodProduct } from '../../common/products.utils';
import ProductVatRate from './ProductVatRate';
import ProductIngredients from './ProductIngredients';
import ProductAllergens from './ProductAllergens';
import ProductNutritionFacts from './ProductNutritionFacts';
import ProductLabels from './ProductLabels';
import ProductDescription from './ProductDescription';
import ShelfLifeDurationDisplay from './ShelfLifeDurationDisplay';
import ProductLastModificationDates from './ProductLastModificationDates';
import ProductPreservationMethod from './ProductPreservationMethod';

interface ProductInfoDialogProps {
  product: ProductFragment;
  open: boolean;
  onClose: () => void;
}

export default function ProductInfoDialog({ product, open, onClose }: ProductInfoDialogProps): React.JSX.Element {
  const { t } = useTranslation(['products', 'product-classification', 'product-classification-tags']);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      sx={{
        '& .MuiPaper-root.MuiDialog-paper': {
          width: {            
            sm: 640,
          },          
        },
      }}
    >
      <DialogTitle>
        <Typography>{t('products:product_details')}</Typography>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', rowGap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
          <Typography variant="headlineSmall" sx={{ mb: 1 }}>
            {product.name}
          </Typography>
          <Chip
            variant="tag"
            label={t(`product-family:${PackagingFamilyTypeIntl[product.sub_family as Product_Sub_Family_Name_Enum]}`)}
          />
        </Box>
        {!!product.labels.length && (
          <ProductLabels labels={product.labels} />
        )}
        {product.generic_product_code && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="titleSmall">
              {t('products:product_nature')}
            </Typography>
            <Typography variant="bodyMedium">
              {t(`product-classification:${product.generic_product_code}`)}
            </Typography>
          </Box>
        )}
        {!!product.classification_tags.length && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="titleSmall">
              {t('products:classification_tags')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {product.classification_tags.map(({ tag_code }) => (
                <Chip
                  key={tag_code}
                  variant="outlinedTag"
                  label={t(`product-classification-tags:${tag_code}`)}
                />
              ))}
            </Box>
          </Box>
        )}
        {product.brand && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="titleSmall">
              {t('products:brand')}
            </Typography>
            <Typography variant="bodyMedium">
              {product.brand}
            </Typography>
          </Box>
        )}
        {product.description && <ProductDescription description={product.description} />}
        <ProductVatRate vatRate={product.vat_rate!} />
        <ShelfLifeDurationDisplay
          titleVariant="titleSmall"
          duration={product.shelf_life_duration}
          durationType={product.shelf_life_duration_type}
          valueTypographyVariant="bodyMedium"
        />
        {product.alcohol_by_volume && ProductUtils.isAlcoholProduct(product.sub_family as Product_Sub_Family_Name_Enum) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="titleSmall">
              {t('products:alcohol_by_volume')}
            </Typography>
            <Typography variant="bodyMedium">
              {product.alcohol_by_volume.toFixed(2)} {t('products:alcohol_by_volume_unit')}
            </Typography>
          </Box>
        )}
        {!isNonFoodProduct(product.sub_family as Product_Sub_Family_Name_Enum) && (
          <ProductPreservationMethod preservationMethod={product.preservation_method} />
        )}
        {(product.ingredients || product.is_exempt_from_ingredient_list) && (
          <ProductIngredients
            ingredients={product.ingredients}
            isExempt={product.is_exempt_from_ingredient_list}
          />
        )}
        {(product.allergens || product.has_no_allergen_declaration) && (
          <ProductAllergens
            allergens={product.allergens}
            hasDeclaration={product.has_no_allergen_declaration}
          />
        )}
        <ProductNutritionFacts product={product} />
        <ProductLastModificationDates
          titleVariant="titleSmall"
          last_price_updated_date={product.last_price_updated_date}
          last_other_updated_date={product.last_other_updated_date}
        />
      </DialogContent>
      <DialogActions sx={{ py: 2, justifyContent: 'end' }}>
        <Button onClick={onClose} variant="text">
          {t('common:close')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          component={Link}
          to={`${RoutePaths.PRODUCT_ADD_INFO}?productId=${product.id}`}
        >
          {t('common:modify')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
